import React, { useState, useEffect } from 'react';
import { Button, Grid, Typography, TextField, FormControl, Select, MenuItem, InputLabel, IconButton, Menu, ListItemIcon, useMediaQuery } from '@mui/material';
import { Add, FilterAlt, MoreVert } from '@mui/icons-material';
import AddUOMModal from './modals/AddUOMModal';
import EditUOMModal from './modals/EditUOMModal';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { Loading } from '../../../components/loading/Loading';
import UnitOfMeasureTable from './UnitOfMeasureTable';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useInventoryContext } from '../../../context/inventory_context/InventoryContext';
import { TablePaginationKeys } from '../../../components/pagination/TablePaginationKeys';
import { handleChangePage, handleChangeRowsPerPage } from '../../../components/pagination/PaginationHelpers';
import FilterDialog from '../../../components/filter_dialog/FilterDialog';
import { handleMenuClick, handleMenuClose } from '../../../components/utils/MenuHandlers';

function UnitOfMeasure() {
    const { organization } = useOrganizationContext();
    const { UOMs } = useInventoryContext();
    const [showAddUOMModal, setShowAddUOMModal] = useState(false);
    const [showEditUOMModal, setShowEditUOMModal] = useState(false);
    const [uomToEdit, setUomToEdit] = useState(null);
    const { hasPermission } = usePermissions();
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const isMobile = useMediaQuery('(max-width:600px)');
    const [anchorEl, setAnchorEl] = useState(null); 
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false); 

    useEffect(() => {
        const savedRowsPerPage = localStorage.getItem(TablePaginationKeys.UnitOfMeasureRowsPerPage);
        if (savedRowsPerPage) {
            setRowsPerPage(parseInt(savedRowsPerPage, 10));
        }
    }, []);

    const handleOpenBottomSheet = () => {
        setIsBottomSheetOpen(true);
    };

    const handleCloseBottomSheet = () => {
        setIsBottomSheetOpen(false);
    };

    const getFilteredUOMs = () => {
        return UOMs
            .filter(uom => uom.id.toLowerCase().includes(searchTerm.toLowerCase()))
            .sort((a, b) => sortOrder === 'asc' ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id));
    };

    return (
        <>
            {organization ? (
                <AppSidebar
                    content={
                        <>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12}>
                                    <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                        {!isMobile && (
                                            <Grid item>
                                                <Typography variant="h5">{t(StringKeys.UNIT_OF_MEASURE)}</Typography>
                                            </Grid>
                                        )}
                                        <Grid item>
                                            <Grid container spacing={2} alignItems="center">
                                                {!isMobile && (
                                                    <>
                                                        <Grid item>
                                                            <TextField
                                                                label={t(StringKeys.SEARCH)}
                                                                variant="outlined"
                                                                size="small"
                                                                value={searchTerm}
                                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                                                                <InputLabel>{t(StringKeys.SORT)}</InputLabel>
                                                                <Select
                                                                    value={sortOrder}
                                                                    onChange={(e) => setSortOrder(e.target.value)}
                                                                    label={t(StringKeys.SORT)}
                                                                >
                                                                    <MenuItem value="asc">A-Z</MenuItem>
                                                                    <MenuItem value="desc">Z-A</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                        {hasPermission(Modules.UNIT_OF_MEASURE, PermissionTypes.WRITE) && (
                                                            <Grid item>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={() => setShowAddUOMModal(true)}
                                                                    startIcon={<Add />}
                                                                >
                                                                    {t(StringKeys.ADD_UOM)}
                                                                </Button>
                                                            </Grid>
                                                        )}
                                                    </>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {isMobile && (
                                        <>
                                            <Typography variant="h6">{t(StringKeys.UNIT_OF_MEASURE)}</Typography>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={10}>
                                                    <TextField
                                                        label={t(StringKeys.SEARCH)}
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                                        <MoreVert />
                                                    </IconButton>
                                                    <Menu
                                                        anchorEl={anchorEl}
                                                        open={Boolean(anchorEl)}
                                                        onClose={() => handleMenuClose(setAnchorEl)}
                                                    >
                                                        <MenuItem onClick={() => { handleOpenBottomSheet(); handleMenuClose(setAnchorEl); }}>
                                                            <ListItemIcon>
                                                                <FilterAlt />
                                                            </ListItemIcon>
                                                            {t(StringKeys.FILTER)}
                                                        </MenuItem>
                                                        <MenuItem onClick={() => { setShowAddUOMModal(true); handleMenuClose(setAnchorEl); }}>
                                                            <ListItemIcon>
                                                                <Add />
                                                            </ListItemIcon>
                                                            {t(StringKeys.ADD_UOM)}
                                                        </MenuItem>
                                                    </Menu>
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>

                                {/* Unit of Measure Table */}
                                <Grid item xs={12}>
                                    <UnitOfMeasureTable 
                                        uoms={getFilteredUOMs()}
                                        page={page}
                                        rowsPerPage={rowsPerPage}
                                        handleChangePage={(event, newPage) => handleChangePage(event, newPage, setPage)}
                                        handleChangeRowsPerPage={(event) => handleChangeRowsPerPage(event, TablePaginationKeys.UnitOfMeasureRowsPerPage, setPage, setRowsPerPage)}
                                    />
                                </Grid>
                            </Grid>
                            <FilterDialog
                                open={isBottomSheetOpen}
                                onClose={handleCloseBottomSheet}
                                sortOrder={sortOrder}
                                setSortOrder={setSortOrder}
                                filters={[]}
                            />
                        </>
                    }
                />
            ) : (
                <Loading />
            )}
            {/* Modals */}
            <AddUOMModal
                show={showAddUOMModal}
                onHide={() => setShowAddUOMModal(false)}
            />
            <EditUOMModal
                show={showEditUOMModal}
                onHide={() => setShowEditUOMModal(false)}
                uom={uomToEdit}
            />
        </>
    );
}

export default UnitOfMeasure;
